import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox/';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';

import { dangerousGoodsPurolator } from '../../../../../../services/other-shipping-options/constants';
import { DangerousGoodsRow } from '../dangerous-goods-row';
import { messages } from './messages';
import styles from './styles.module.css';

const typesOfDangerItems = [
  {
    key: 'FullyRegulated',
    label: <FormattedMessage {...messages.fullRegulated} />,
  },
  { key: 'UN3373', label: 'UN3373' },
  { key: 'UN1845', label: 'UN1845' },
  {
    key: 'LessThan500kgExempt',
    label: <FormattedMessage {...messages.lessThan500kgExempt} />,
  },
  {
    key: 'LimitedQuantities',
    label: <FormattedMessage {...messages.limitedQuantities} />,
  },
];

const modeTypeItems = [
  { key: 'Air', label: <FormattedMessage {...messages.air} /> },
  { key: 'Ground', label: <FormattedMessage {...messages.ground} /> },
];

/**
 * @typedef {import('ui-components/components/checkbox').CheckboxChangeHandler} CheckboxChangeHandler
 */

/**
 * Component for handling dangerous goods shipping option.
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.label - The label for the checkbox.
 * @param {boolean} props.checked - The checked state of the checkbox.
 * @param {{class: string, mode: string}} props.optionData - The data for the dangerous goods option.
 * @param {CheckboxChangeHandler} props.onCheck - The function to handle checkbox change event.
 * @param {(arg: { id: string, value: { class: string, mode: string }}) => void} props.updateOption - The function to update the dangerous goods option.
 * @returns {JSX.Element} The rendered DangerousGoods component.
 */
export const DangerousGoodsPurolator = ({ label, checked, onCheck, optionData, updateOption }) => {
  const onChange = (property, optionKey) => {
    return () => {
      const newValue = {
        ...optionData,
        [property]: optionKey,
      };
      updateOption({ id: dangerousGoodsPurolator, value: newValue });
    };
  };

  const typeOfDangerItem = typesOfDangerItems.find((item) => item.key === optionData?.class);
  const modeItem = modeTypeItems.find((item) => item.key === optionData?.mode);

  return (
    <div>
      <Checkbox checked={checked} label={label} onChange={onCheck} />
      {checked ? (
        <div>
          <DangerousGoodsRow>
            <Text
              className={styles['dangerous-goods-type-field']}
              htmlFor='type-of-danger-dropdown'
              textType={TextType.Label}
              value={messages.dangerousGoodsType}
            />
            <DropdownMenu className={styles['dropdown-menu']} dropdownMenuShouldHaveWidthOfButton id='type-of-danger-dropdown'>
              <DropdownMenuToggler>
                {typeOfDangerItem?.label ?? <FormattedMessage {...messages.defaultDangerTypeOption} />}
              </DropdownMenuToggler>
              {typesOfDangerItems.map((option) => (
                <DropdownMenuItem key={option.key} onClick={onChange('class', option.key)}>
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </DangerousGoodsRow>
          <DangerousGoodsRow>
            <Text
              className={styles['dangerous-goods-type-field']}
              htmlFor='mode-dropdown'
              textType={TextType.Label}
              value={messages.modeType}
            />
            <DropdownMenu className={styles['dropdown-menu']} dropdownMenuShouldHaveWidthOfButton id='mode-dropdown'>
              <DropdownMenuToggler>{modeItem?.label ?? <FormattedMessage {...messages.defaultModeTypeOption} />}</DropdownMenuToggler>
              {modeTypeItems.map((option) => (
                <DropdownMenuItem key={option.key} onClick={onChange('mode', option.key)}>
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </DangerousGoodsRow>
        </div>
      ) : null}
    </div>
  );
};
